import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

/* ═══════════════════════════════════════════
   SECTION 1: 3D TRUCK (GLB model)
   ═══════════════════════════════════════════ */
function TruckScene() {
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const prevMouse = useRef({ x: 0, y: 0 });
  const rotationRef = useRef({ x: 0, y: -0.8 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 200);
    camera.position.set(8, 3, 10);
    camera.lookAt(0, 1.5, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x060a08, 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);

    /* ─── Lighting ─── */
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(8, 12, 8);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(2048, 2048);
    dirLight.shadow.camera.near = 0.1;
    dirLight.shadow.camera.far = 50;
    dirLight.shadow.camera.left = -15;
    dirLight.shadow.camera.right = 15;
    dirLight.shadow.camera.top = 15;
    dirLight.shadow.camera.bottom = -15;
    scene.add(dirLight);

    // Green accent lights
    const greenLight = new THREE.PointLight(0x16b724, 0.8, 25);
    greenLight.position.set(-5, 4, 6);
    scene.add(greenLight);

    const greenLight2 = new THREE.PointLight(0x4dc247, 0.4, 20);
    greenLight2.position.set(5, 2, -5);
    scene.add(greenLight2);

    // Fill light from below
    const fillLight = new THREE.PointLight(0x223322, 0.3, 15);
    fillLight.position.set(0, -1, 0);
    scene.add(fillLight);

    /* ─── Ground ─── */
    const groundGeo = new THREE.PlaneGeometry(50, 50);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x080c08,
      roughness: 0.85,
      metalness: 0.15,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);

    // Subtle grid
    const gridHelper = new THREE.GridHelper(30, 60, 0x16b724, 0x16b724);
    gridHelper.position.y = 0.01;
    gridHelper.material.opacity = 0.04;
    gridHelper.material.transparent = true;
    scene.add(gridHelper);

    /* ─── Load GLB model ─── */
    const loader = new GLTFLoader();
    let truckModel = null;

    loader.load(
      '/models/truck.glb',
      (gltf) => {
        truckModel = gltf.scene;

        // Calculate bounding box to center and scale
        const box = new THREE.Box3().setFromObject(truckModel);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // Scale to fit nicely (target about 8 units long)
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 8 / maxDim;
        truckModel.scale.setScalar(scale);

        // Center the model
        truckModel.position.x = -center.x * scale;
        truckModel.position.y = -box.min.y * scale;
        truckModel.position.z = -center.z * scale;

        // Enable shadows on all meshes
        truckModel.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        // Wrap in group for rotation
        const wrapper = new THREE.Group();
        wrapper.add(truckModel);
        scene.add(wrapper);
        truckModel = wrapper;

        setLoading(false);
      },
      (progress) => {
        // Loading progress
      },
      (error) => {
        console.error('Error loading truck model:', error);
        setLoading(false);
      }
    );

    /* ─── Floating particles ─── */
    const pCount = 100;
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount; i++) {
      pPos[i * 3] = (Math.random() - 0.5) * 30;
      pPos[i * 3 + 1] = Math.random() * 15;
      pPos[i * 3 + 2] = (Math.random() - 0.5) * 30;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: 0x16b724, size: 0.05, transparent: true, opacity: 0.3,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Points(pGeo, pMat));

    /* ─── Mouse interaction ─── */
    const onDown = (e) => {
      isDragging.current = true;
      const x = e.clientX || (e.touches && e.touches[0].clientX);
      const y = e.clientY || (e.touches && e.touches[0].clientY);
      prevMouse.current = { x, y };
    };
    const onMove = (e) => {
      if (!isDragging.current) return;
      const x = e.clientX || (e.touches && e.touches[0].clientX);
      const y = e.clientY || (e.touches && e.touches[0].clientY);
      const dx = x - prevMouse.current.x;
      const dy = y - prevMouse.current.y;
      rotationRef.current.y += dx * 0.008;
      rotationRef.current.x += dy * 0.004;
      rotationRef.current.x = Math.max(-0.3, Math.min(0.6, rotationRef.current.x));
      prevMouse.current = { x, y };
    };
    const onUp = () => { isDragging.current = false; };

    renderer.domElement.addEventListener('mousedown', onDown);
    renderer.domElement.addEventListener('mousemove', onMove);
    renderer.domElement.addEventListener('mouseup', onUp);
    renderer.domElement.addEventListener('mouseleave', onUp);
    renderer.domElement.addEventListener('touchstart', onDown, { passive: true });
    renderer.domElement.addEventListener('touchmove', onMove, { passive: true });
    renderer.domElement.addEventListener('touchend', onUp);

    /* ─── Animation ─── */
    let animId;
    const clock = new THREE.Clock();

    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      if (truckModel) {
        // Auto-rotate when not dragging
        if (!isDragging.current) {
          rotationRef.current.y += 0.003;
        }
        truckModel.rotation.y += (rotationRef.current.y - truckModel.rotation.y) * 0.06;
        truckModel.rotation.x += (rotationRef.current.x - truckModel.rotation.x) * 0.06;
      }

      // Green light pulse
      greenLight.intensity = 0.7 + Math.sin(t * 1.5) * 0.3;

      renderer.render(scene, camera);
    };
    animate();

    /* ─── Resize ─── */
    const onResize = () => {
      const ww = container.clientWidth;
      const hh = container.clientHeight;
      camera.aspect = ww / hh;
      camera.updateProjectionMatrix();
      renderer.setSize(ww, hh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#4dc247', fontSize: 14, fontWeight: 500,
          zIndex: 2,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, border: '3px solid rgba(22,183,36,0.2)',
              borderTopColor: '#4dc247', borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 12px',
            }} />
            Завантаження моделі...
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', cursor: 'grab' }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════
   SECTION 2: INFINITE ROAD
   ═══════════════════════════════════════════ */
function RoadScene() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x040806, 0.006);

    const camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 500);
    camera.position.set(0, 3.5, 5);
    camera.lookAt(0, 1.5, -100);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x040806, 1);
    container.appendChild(renderer.domElement);

    /* ─── Lighting ─── */
    const ambient = new THREE.AmbientLight(0x223322, 0.5);
    scene.add(ambient);

    const moonLight = new THREE.DirectionalLight(0x8899aa, 0.3);
    moonLight.position.set(5, 20, -10);
    scene.add(moonLight);

    /* ─── Road ─── */
    const roadLength = 800;
    const roadGeo = new THREE.PlaneGeometry(12, roadLength, 1, 1);
    const roadMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.95,
      metalness: 0.05,
    });
    const road = new THREE.Mesh(roadGeo, roadMat);
    road.rotation.x = -Math.PI / 2;
    road.position.set(0, 0, -roadLength / 2 + 10);
    scene.add(road);

    /* ─── Lane markings ─── */
    const markings = [];
    const markMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 });

    // Center dashed line
    const markSpacing = 5;
    const markCount = 84;
    const markLoopLen = markCount * markSpacing;
    for (let i = 0; i < markCount; i++) {
      const markGeo = new THREE.PlaneGeometry(0.15, 3);
      const mark = new THREE.Mesh(markGeo, markMat);
      mark.rotation.x = -Math.PI / 2;
      mark.position.set(0, 0.01, -i * markSpacing);
      scene.add(mark);
      markings.push(mark);
    }

    // Side lines (solid) - always on top, no fog
    const sideMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, fog: false, depthWrite: false });
    [-5.5, 5.5].forEach(x => {
      const sideGeo = new THREE.PlaneGeometry(0.15, roadLength);
      const side = new THREE.Mesh(sideGeo, sideMat);
      side.rotation.x = -Math.PI / 2;
      side.position.set(x, 0.08, -roadLength / 2 + 10);
      side.renderOrder = 1;
      scene.add(side);
    });

    // Edge/shoulder lines (green glow) - always on top, no fog
    const edgeMat = new THREE.MeshBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.2, fog: false, depthWrite: false });
    [-6, 6].forEach(x => {
      const edgeGeo = new THREE.PlaneGeometry(0.12, roadLength);
      const edge = new THREE.Mesh(edgeGeo, edgeMat);
      edge.rotation.x = -Math.PI / 2;
      edge.position.set(x, 0.08, -roadLength / 2 + 10);
      edge.renderOrder = 1;
      scene.add(edge);
    });

    /* ─── Road posts with lights ─── */
    const postGroups = [];
    const postSpacing = 10;
    const postCount = 42;
    const postLoopLen = postCount * postSpacing;
    for (let i = 0; i < postCount; i++) {
      [-7, 7].forEach(x => {
        const group = new THREE.Group();
        group.position.set(0, 0, -i * postSpacing);

        // Post
        const postGeo = new THREE.CylinderGeometry(0.05, 0.06, 2, 6);
        const postMat = new THREE.MeshStandardMaterial({ color: 0x333333 });
        const post = new THREE.Mesh(postGeo, postMat);
        post.position.set(x, 1, 0);
        group.add(post);

        // Reflector on post
        const refGeo = new THREE.BoxGeometry(0.08, 0.2, 0.08);
        const refMat = new THREE.MeshBasicMaterial({ color: x > 0 ? 0x16b724 : 0xff4400, transparent: true, opacity: 0.7 });
        const ref = new THREE.Mesh(refGeo, refMat);
        ref.position.set(x, 1.5, 0);
        group.add(ref);

        scene.add(group);
        postGroups.push(group);
      });
    }

    /* ─── City road signs ─── */
    const cityNames = [
      'КИЇВ', 'ЛЬВІВ', 'ОДЕСА', 'ХАРКІВ', 'ДНІПРО',
      'ВАРШАВА', 'ПРАГА', 'БЕРЛІН', 'БУДАПЕШТ', 'ВІДЕНЬ',
      'РИГА', 'ТАЛЛІНН', 'ВІЛЬНЮС', 'БУХАРЕСТ', 'СОФІЯ',
      'РИМ', 'ПАРИЖ', 'АМСТЕРДАМ', 'БРАТИСЛАВА', 'БРЮССЕЛЬ',
    ];
    const signSigns = [];
    const signCount = 5;
    const signSpacing = 120;
    const signLoopLen = signCount * signSpacing;

    for (let i = 0; i < signCount; i++) {
      const group = new THREE.Group();
      group.position.set(0, 0, -i * signSpacing - 60);

      const side = (i % 2 === 0) ? 8 : -8;

      // Sign post (tall) - offset to the outer edge of the sign
      const poleMat = new THREE.MeshStandardMaterial({ color: 0x555555, metalness: 0.6, roughness: 0.4 });
      const poleGeo = new THREE.CylinderGeometry(0.06, 0.06, 5, 8);
      const pole = new THREE.Mesh(poleGeo, poleMat);
      pole.position.set(side + (side > 0 ? 1.6 : -1.6), 2.5, 0);
      group.add(pole);

      // Sign board - faces camera (along Z axis)
      const boardGeo = new THREE.BoxGeometry(3.5, 1.6, 0.02);
      const boardMat = new THREE.MeshStandardMaterial({ color: 0x0d6e18, roughness: 0.5, metalness: 0.3 });
      const board = new THREE.Mesh(boardGeo, boardMat);
      board.position.set(side, 4.2, 0);
      group.add(board);

      // White border frame - faces camera
      const borderMat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false });
      const hBorderGeo = new THREE.BoxGeometry(3.6, 0.05, 0.03);
      const topBorder = new THREE.Mesh(hBorderGeo, borderMat);
      topBorder.position.set(side, 5.0, 0);
      group.add(topBorder);
      const botBorder = new THREE.Mesh(hBorderGeo, borderMat);
      botBorder.position.set(side, 3.4, 0);
      group.add(botBorder);
      const vBorderGeo = new THREE.BoxGeometry(0.05, 1.65, 0.03);
      const leftBorder = new THREE.Mesh(vBorderGeo, borderMat);
      leftBorder.position.set(side - 1.78, 4.2, 0);
      group.add(leftBorder);
      const rightBorder = new THREE.Mesh(vBorderGeo, borderMat);
      rightBorder.position.set(side + 1.78, 4.2, 0);
      group.add(rightBorder);

      // City name text using canvas texture
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');

      const cityName = cityNames[Math.floor(Math.random() * cityNames.length)];
      const km = Math.floor(Math.random() * 300 + 20);

      ctx.fillStyle = '#0d6e18';
      ctx.fillRect(0, 0, 512, 200);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 72px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cityName, 256, 75);
      ctx.font = '40px Arial, sans-serif';
      ctx.fillText(km + ' km →', 256, 150);

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      const textGeo = new THREE.PlaneGeometry(3.3, 1.4);
      const textMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, fog: false });

      // Front face (facing camera)
      const textFront = new THREE.Mesh(textGeo, textMat);
      textFront.position.set(side, 4.2, 0.02);
      group.add(textFront);

      // Back face
      const textBack = new THREE.Mesh(textGeo, textMat);
      textBack.position.set(side, 4.2, -0.02);
      textBack.rotation.y = Math.PI;
      group.add(textBack);

      scene.add(group);
      signSigns.push({ group });
    }

    /* ─── Ground / terrain sides ─── */
    [-15, 15].forEach(x => {
      const terrGeo = new THREE.PlaneGeometry(20, roadLength, 1, 1);
      const terrMat = new THREE.MeshStandardMaterial({
        color: 0x0a1a0a,
        roughness: 1,
        metalness: 0,
      });
      const terr = new THREE.Mesh(terrGeo, terrMat);
      terr.rotation.x = -Math.PI / 2;
      terr.position.set(x, -0.15, -roadLength / 2 + 10);
      scene.add(terr);
    });

    /* ─── Stars ─── */
    const starCount = 500;
    const starPos = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      starPos[i * 3] = (Math.random() - 0.5) * 200;
      starPos[i * 3 + 1] = 20 + Math.random() * 60;
      starPos[i * 3 + 2] = -Math.random() * 300;
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    const starMat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.3,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Points(starGeo, starMat));

    /* ─── Oncoming headlight particles ─── */
    const headlightCount = 6;
    const headlights = [];
    for (let i = 0; i < headlightCount; i++) {
      const hlGeo = new THREE.SphereGeometry(0.15, 8, 8);
      const hlMat = new THREE.MeshBasicMaterial({
        color: 0xffffcc,
        transparent: true,
        opacity: 0,
      });
      const hl = new THREE.Mesh(hlGeo, hlMat);
      hl.position.set(-3.5 + (Math.random() - 0.5) * 0.5, 1.5, -200 - Math.random() * 200);
      scene.add(hl);

      // Point light for glow
      const pl = new THREE.PointLight(0xffffcc, 0, 15);
      hl.add(pl);

      headlights.push({ mesh: hl, mat: hlMat, light: pl, speed: 30 + Math.random() * 20, offset: i * 60 });
    }

    /* ─── Dashboard hint ─── */
    // Speed lines (motion blur effect)
    const speedLines = [];
    for (let i = 0; i < 30; i++) {
      const slGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, -2 - Math.random() * 3),
      ]);
      const slMat = new THREE.LineBasicMaterial({
        color: 0x16b724,
        transparent: true,
        opacity: 0.05 + Math.random() * 0.1,
      });
      const sl = new THREE.Line(slGeo, slMat);
      sl.position.set(
        (Math.random() - 0.5) * 16,
        Math.random() * 4,
        -Math.random() * 100
      );
      scene.add(sl);
      speedLines.push({ line: sl, baseZ: sl.position.z, speed: 0.5 + Math.random() * 1 });
    }

    /* ─── Animation ─── */
    let animId;
    const speed = 40;
    let totalDist = 0;

    const animate = () => {
      animId = requestAnimationFrame(animate);
      const delta = 0.016;
      const t = performance.now() * 0.001;
      totalDist += speed * delta;

      // Move markings - absolute position based on totalDist
      markings.forEach((m, i) => {
        const baseZ = -i * markSpacing;
        const shifted = ((baseZ + totalDist) % markLoopLen + markLoopLen) % markLoopLen;
        m.position.z = shifted - markLoopLen + 15;
      });

      // Move posts - absolute position based on totalDist
      postGroups.forEach((p, i) => {
        const pairIndex = Math.floor(i / 2);
        const baseZ = -pairIndex * postSpacing;
        const shifted = ((baseZ + totalDist) % postLoopLen + postLoopLen) % postLoopLen;
        p.position.z = shifted - postLoopLen + 15;
      });

      // Move city signs
      signSigns.forEach((s, i) => {
        const baseZ = -i * signSpacing - 60;
        const shifted = ((baseZ + totalDist) % signLoopLen + signLoopLen) % signLoopLen;
        const newZ = shifted - signLoopLen + 15;

        // When sign loops back, change city name
        if (newZ > s.group.position.z + signLoopLen * 0.5) {
          // Pick new random city & km
          const cityName = cityNames[Math.floor(Math.random() * cityNames.length)];
          const km = Math.floor(Math.random() * 300 + 20);
          // Update canvas textures (the two text planes)
          s.group.children.forEach(child => {
            if (child.material && child.material.map) {
              const canvas = child.material.map.image;
              if (canvas && canvas.getContext) {
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#0d6e18';
                ctx.fillRect(0, 0, 512, 200);
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 64px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(cityName, 256, 75);
                ctx.font = '36px Arial, sans-serif';
                ctx.fillText(km + ' km', 256, 145);
                child.material.map.needsUpdate = true;
              }
            }
          });
        }
        s.group.position.z = newZ;
      });

      // Headlights coming toward camera
      headlights.forEach(hl => {
        hl.mesh.position.z += hl.speed * delta;
        const dist = hl.mesh.position.z;
        if (dist < -20) {
          hl.mat.opacity = Math.max(0, 0.8 * (1 - Math.abs(dist + 20) / 180));
          hl.light.intensity = hl.mat.opacity * 2;
        } else {
          hl.mat.opacity = Math.max(0, 1 - dist / 10);
          hl.light.intensity = hl.mat.opacity * 3;
        }
        if (hl.mesh.position.z > 15) {
          hl.mesh.position.z = -300 - Math.random() * 100;
          hl.mesh.position.x = -3.5 + (Math.random() - 0.5) * 0.8;
        }
      });

      // Speed lines
      speedLines.forEach(sl => {
        sl.line.position.z += sl.speed;
        if (sl.line.position.z > 10) sl.line.position.z = sl.baseZ;
      });

      // Gentle camera sway
      camera.position.x = Math.sin(t * 0.3) * 0.15;
      camera.position.y = 3.5 + Math.sin(t * 0.5) * 0.1;

      renderer.render(scene, camera);
    };
    animate();

    /* ─── Resize ─── */
    const onResize = () => {
      const ww = container.clientWidth;
      const hh = container.clientHeight;
      camera.aspect = ww / hh;
      camera.updateProjectionMatrix();
      renderer.setSize(ww, hh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%' }}
    />
  );
}


/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */
function ThreeDemo() {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <section style={{
      minHeight: '100vh',
      padding: isMobile ? '70px 0 0' : '80px 0 0',
      background: '#040806',
    }}>
      <div style={{
        width: '100%',
        height: isMobile ? '80vh' : 'calc(100vh - 80px)',
        position: 'relative',
      }}>
        <RoadScene />

        <div style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          right: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}>
          <div>
            <div style={{
              color: '#4dc247',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              Нескінченна подорож
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
              Вид з кабіни водія
            </div>
          </div>
          <div style={{
            color: '#4dc247',
            fontSize: 28,
            fontWeight: 700,
            fontFamily: 'monospace',
            textShadow: '0 0 10px rgba(77,194,71,0.5)',
          }}>
            90 km/h
          </div>
        </div>
      </div>
    </section>
  );
}

export default ThreeDemo;
